import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Appointment, Mechanic, Task, TaskStatus } from '../../../types';
import { TaskFormState } from './helpers';

interface TaskFormDialogProps {
  taskFormDialogOpen: boolean;
  setTaskFormDialogOpen: (open: boolean) => void;
  editingTask: Task | null;
  taskForm: TaskFormState;
  setTaskForm: (form: TaskFormState) => void;
  selectedAppointment: Appointment | null;
  mechanics: Mechanic[];
  handleSaveTask: () => void;
}

/** Create or edit one task. */
const TaskFormDialog: React.FC<TaskFormDialogProps> = ({
  taskFormDialogOpen,
  setTaskFormDialogOpen,
  editingTask,
  taskForm,
  setTaskForm,
  selectedAppointment,
  mechanics,
  handleSaveTask,
}) => {
  return (
    <Dialog open={taskFormDialogOpen} onClose={() => setTaskFormDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingTask ? 'Edit Task' : 'Create New Task'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Task Title"
            value={taskForm.title}
            onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Description (Optional)"
            value={taskForm.description}
            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={taskForm.status}
              label="Status"
              onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value as TaskStatus })}
            >
              <MenuItem value={TaskStatus.PENDING}>Pending</MenuItem>
              <MenuItem value={TaskStatus.IN_PROGRESS}>In Progress</MenuItem>
              <MenuItem value={TaskStatus.COMPLETED}>Completed</MenuItem>
              <MenuItem value={TaskStatus.CANCELLED}>Canceled</MenuItem>
            </Select>
          </FormControl>
          {selectedAppointment && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Assign To Mechanic (Optional)</InputLabel>
              <Select
                value={taskForm.assignedToId}
                label="Assign To Mechanic (Optional)"
                onChange={(e) => setTaskForm({ ...taskForm, assignedToId: e.target.value })}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mechanics
                  .filter(mechanic => mechanic.shopId === selectedAppointment.shopId)
                  .map((mechanic) => (
                    <MenuItem key={mechanic.id} value={mechanic.userId}>
                      {mechanic.user?.name} ({mechanic.specialization})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setTaskFormDialogOpen(false)}>
          Cancel
        </Button>
        <Button onClick={handleSaveTask} variant="contained" disabled={!taskForm.title.trim()}>
          {editingTask ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskFormDialog;
